import { errorSafe } from 'app/infrastructure/redux';
import {
  ACTION, RequestCaseViewTableActionType, CaseViewTableResultAppendActionType, CaseViewTableResultReplaceActionType,
  CaseViewTableClearActionType,
  FailExecuteFavouriteActionType,
  CaseViewTableRemoveItemActionType,
  CaseViewTableRequestRemoveItemActionType,
  CaseViewTableRequestSingleSupplierUrlActionType,
  CaseViewTableReceiveSingleSupplierUrlActionType
}
  from 'app/area/case-report/case-table-view/action-types';
import { api } from 'app/area/case-report/case-table-view/api';

import { api as caseListingApi } from 'app/area/case-report/case-listing/api';
import { PayloadCaseTableViewResultType } from 'app/area/case-report/case-table-view/types-store';
import { CASE_TABLE_VIEW_MAX_RESULTS } from 'app/area/case-report/case-table-view/constants';
import { caseDataToStore } from 'app/infrastructure/store/normalize';
import uniqBy from 'lodash/uniqBy';
import { UiState } from 'app/infrastructure/ui/form/types';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { caseListingTranslation } from 'app/area/case-report/case-listing/constants';
import { ExecuteCaseTableViewReportType } from 'app/area/case-report/case-table-view/types-api';
import { AddRiskEvaluationInitialValues } from 'app/area/case/features/loss-potential-evaluation/types';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { getUserMessage } from 'app/infrastructure/notifications/user-messages/get-user-message';
import { sectionId, tableName } from 'app/area/case/case-data-model';
import * as navigate from 'app/area/shared/navigation';
import { api as lossPotentialEvaluationApi } from 'app/area/case/features/loss-potential-evaluation/api';
import { ReceiveCopyRiskEvaluation, RequestCopyRiskEvaluation } from 'app/area/case/features/loss-potential-evaluation/action-types';

export const executeCaseTableViewReport = ({
  definition,
  instanceKey,
  startIndex = 0,
  overrideExecuteFavourite
}: ExecuteCaseTableViewReportType) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    dispatch(RequestCaseViewTableAction(instanceKey));
    try {
      let response;
      // Dashboard using different APIs to load the data
      if (overrideExecuteFavourite) {
        response = await overrideExecuteFavourite(definition, startIndex, CASE_TABLE_VIEW_MAX_RESULTS, true);
      } else {
        response = await api.executeCaseTableViewReport(definition, startIndex, CASE_TABLE_VIEW_MAX_RESULTS);
      }
      const { results, messages } = response;
      const replaceResult = !!startIndex;
      const { mainEntityTypePath = [], includedFields = [] } = response?.metadata || {};
      const tblTransFields = mainEntityTypePath.length > 0
      ? includedFields.filter(field => field?.entityTypePath?.every(item => mainEntityTypePath.includes(item))): [];
      const uniqueFields = uniqBy(tblTransFields, 'columnName');
      const metadata = { ...response.metadata, includedFields: uniqueFields };
      const entities = results?.map(caseDataToStore);
      if (entities) {
        replaceResult ? dispatch(CaseViewTableResultAppendAction({
          instanceKey,
          caseTableViewResults: {
            entities,
            metadata,
            messages
          }
        })) :
          dispatch(CaseViewTableResultReplaceAction({
            instanceKey,
            caseTableViewResults: {
              entities,
              metadata,
              messages
            }
          }));
      }
      else {
        dispatch(CaseViewTableResultAppendAction({
          instanceKey,
          caseTableViewResults: {
            entities: [],
            metadata,
            messages
          }
        }));
      }
    }
    catch (error) {
      if (error?.userMessages?.length) {
        dispatch(CaseViewTableFailExecuteFavouriteAction(instanceKey, error.userMessages.map(m => m.message)));
      }
    }
  });
};

export const deleteCaseById = (instanceKey: string, caseId: number) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    dispatch(CaseViewTableRequestRemoveAction(instanceKey, caseId));

    try {
      const result = await caseListingApi.deleteCase(caseId) as any;
      const areaState = getState().favourite as UiState;
      const translationDescription = createDescriptionSelector(areaState.texts);
      if (result && result.status === 204) {
        dispatch(new SuccessNotification(
          translationDescription(caseListingTranslation.case_deleted_success)
        ));
        dispatch(CaseViewTableRemoveAction(instanceKey, caseId));
      }
    } catch (err) {
      dispatch({
        type: 'ERROR',
        error: err
      });
    }
  });
};

export const getSingleSupplierUrl = (instanceKey: string) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    dispatch(CaseViewTableRequestSingleSupplierUrlAction(instanceKey));
    try {
      const singleSupplierUrl = await api.getCaseTableViewSingleSupplierUrl(1);
      dispatch(CaseViewTableReceiveSingleSupplierUrlAction(instanceKey, singleSupplierUrl));
    } catch (err) {
      dispatch({
        type: 'ERROR',
        error: err
      });
    }
  });
};


const toasts = toastFactory();
export const copyRiskEvaluation = (instanceKey: string, caseId: number, riskMatrixInstance: number,
  instanceCopyCmd: number, initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean) => {
  return errorSafe(async (dispatch: any, getState: any) => {
    try {
      const utcOffset = getState().app.settings.utcOffset;
      const navigateToRiskEvaluationSection = (draftId: number) => copyToResidual ?
        navigate.toCaseAddRiskEvaluations(draftId, sectionId[tableName.lossPotentialEvaluation].section) :
        navigate.toCaseAddLossPotentialEvaluation(draftId, sectionId[tableName.lossPotentialEvaluation].section);

      dispatch(new RequestCopyRiskEvaluation());
      const response = await lossPotentialEvaluationApi.addRiskEvaluationRead(caseId, riskMatrixInstance, instanceCopyCmd, initialValues,
        { utcOffset },
        copyToResidual);
      const pathname = new URL(response.headers.get('Location'))?.pathname?.split('/');
      const draftIdFromResponse = Number(pathname?.[pathname.length - 1]);
      dispatch(new ReceiveCopyRiskEvaluation());
      navigateToRiskEvaluationSection(draftIdFromResponse);

    } catch (err) {
      toasts.dispatchError(getUserMessage(err.userMessages));
    }
  });
};


export const ClearCaseViewTableAction = (instanceKey: string) => {
  return (dispatch: any, getState: any) => {
    dispatch(CaseViewTableClearAction(instanceKey));
  };
};

export const RequestCaseViewTableAction = (instanceKey: string): RequestCaseViewTableActionType => (
  {
    type: ACTION.REQUEST_CASE_VIEW_TABLE,
    payload: {
      instanceKey
    }
  }
);

export const CaseViewTableResultReplaceAction = ({ instanceKey, caseTableViewResults }:
  PayloadCaseTableViewResultType):
  CaseViewTableResultReplaceActionType => (
  {
    type: ACTION.REPLACE_CASE_VIEW_TABLE,
    payload: {
      instanceKey, caseTableViewResults
    }
  }
);

export const CaseViewTableResultAppendAction = ({ instanceKey, caseTableViewResults }:
  PayloadCaseTableViewResultType):
  CaseViewTableResultAppendActionType => (
  {
    type: ACTION.APPEND_CASE_VIEW_TABLE,
    payload: {
      instanceKey, caseTableViewResults
    }
  }
);

export const CaseViewTableFailExecuteFavouriteAction = (instanceKey: string, errorsExecuteFailed: string[]):
  FailExecuteFavouriteActionType => (
  {
    type: ACTION.FAIL_EXECUTE_FAVOURITE_CASE_VIEW_TABLE,
    payload: {
      instanceKey,
      errorsExecuteFailed
    }
  }
);

export const CaseViewTableClearAction = (instanceKey: string): CaseViewTableClearActionType => (
  {
    type: ACTION.CLEAR_CASE_VIEW_TABLE,
    payload: {
      instanceKey
    }
  }
);

export const CaseViewTableRequestRemoveAction = (instanceKey: string, caseId: number): CaseViewTableRequestRemoveItemActionType => (
  {
    type: ACTION.REQUEST_REMOVE_ITEM_CASE_VIEW_TABLE,
    payload: {
      instanceKey,
      caseId
    }
  }
);

export const CaseViewTableRemoveAction = (instanceKey: string, caseId: number): CaseViewTableRemoveItemActionType => (
  {
    type: ACTION.REMOVE_ITEM_CASE_VIEW_TABLE,
    payload: {
      instanceKey,
      caseId
    }
  }
);


export const CaseViewTableRequestSingleSupplierUrlAction = (instanceKey: string): CaseViewTableRequestSingleSupplierUrlActionType => (
  {
    type: ACTION.REQUEST_SINGLE_SUPPLIER_URL_CASE_VIEW_TABLE,
    payload: {
      instanceKey
    }
  }
);

export const CaseViewTableReceiveSingleSupplierUrlAction = (instanceKey: string, singleSupplierUrl: string): 
  CaseViewTableReceiveSingleSupplierUrlActionType => (
  {
    type: ACTION.RECEIVE_SINGLE_SUPPLIER_URL_CASE_VIEW_TABLE,
    payload: {
      instanceKey,
      singleSupplierUrl
    }
  }
);
